'use client';

import { getQueryClient } from 'Shared/network';
import { setRateLimitListener } from 'Shared/network/rateLimitSignal';
import { resolveRetryAt } from 'Shared/network/tooManyRequests';
import { isPassHandledError, reportApiWarning, toQueryScope } from 'Shared/observability';
import { LINK } from '@constants';
import { MutationCache, QueryCache } from '@tanstack/react-query';
import { useGlobalModalStore, useRateLimitOverlayStoreBase } from 'app/_hook';
import { AxiosError } from 'axios';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  AuthorizationError,
  errorMessage,
  NetworkError,
  PASS_ERROR_MESSAGE,
  TOKEN_NOT_EXIST,
  UNKNOWN_ERROR_MESSAGE,
  UnknownError,
} from 'viewup-lib/common';
import {
  defaultInfiniteQueryPolicy,
  defaultMutationPolicy,
  defaultQueryPolicy,
  SDQueryProvider,
} from 'viewup-lib/react';
import { MSWProvider } from './MSWProvider';

const DynamicQueryDevtools = dynamic(
  () => import('./QueryDevtools').then((mod) => mod.QueryDevtools),
  { ssr: false },
);

// https://tanstack.com/query/v5/docs/react/guides/advanced-ssr
export function QueryProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const { getValue } = useGlobalModalStore();
  const { setAlertModal } = getValue('actions');

  const [showDevtools, setShowDevtools] = useState(false);

  const queryClient = useMemo(() => {
    return getQueryClient({
      defaultOnError: (error) => {
        if (error instanceof AxiosError) {
          if (error.response?.status === 429 && error.response.data?.code === 'tooManyRequests') {
            useRateLimitOverlayStoreBase
              .getState()
              .actions.trigger(resolveRetryAt(error.response.data));
            return;
          }
          const errorCode = error.response?.data.code;
          const msg = errorMessage[errorCode];
          if (msg) {
            setAlertModal({
              content: msg || '알 수 없는 에러가 발생했습니다.',
            });
          }
        }
      },
      mutationCache: new MutationCache({
        onError(error, _variables, _context, mutation) {
          // 아래 모달/리다이렉트 로직에 early return이 여러 개라 최상단에서 리포트해야 누락이 없다.
          // PASS_ERROR_MESSAGE로 삼켜지는 코드는 에러가 아니라 정상 사용자 플로우라 제외한다.
          if (!isPassHandledError(error, mutation?.meta)) {
            reportApiWarning('mutation', error);
          }

          if (
            error instanceof AxiosError &&
            error.response?.status === 429 &&
            error.response.data?.code === 'tooManyRequests'
          ) {
            useRateLimitOverlayStoreBase
              .getState()
              .actions.trigger(resolveRetryAt(error.response.data));
            return;
          }
          const errorCode = (error as AxiosError<{ code: string }>).response?.data?.code || '';
          const errorMessage = mutation?.meta?.errorMessage?.[errorCode] || '';
          const link = mutation?.meta?.link?.[errorCode] || '';

          if (error instanceof NetworkError) {
            setAlertModal({
              content: `네트워크 오류가 발생되었습니다.\n네트워크 문제로 서버와 연결이 끊어졌어요.`,
              title: '오류가 발생했습니다.',
            });
            return;
          }

          if (error instanceof AuthorizationError && error.message === TOKEN_NOT_EXIST) {
            window.location.href = LINK.HOME;
            return;
          }

          if (error instanceof UnknownError) {
            setAlertModal({
              actionButtonText: '1:1 문의하기',
              content: '잠시 후 다시 시도해주시고 지속적으로 발생 시\n1:1 문의 주세요.',
              onClickActionButton: () => {
                router.push(LINK.KAKAO);
              },
              title: '일시적 오류가 발생했어요.',
            });
            return;
          }

          if (errorMessage === PASS_ERROR_MESSAGE) return;

          if (errorMessage === UNKNOWN_ERROR_MESSAGE) {
            setAlertModal({
              actionButtonText: '1:1 문의하기',
              content: '잠시 후 다시 시도해주시고 지속적으로 발생 시\n1:1 문의 주세요.',
              onClickActionButton: () => {
                router.push(LINK.KAKAO);
              },
              title: '일시적 오류가 발생했어요.',
            });
            return;
          }
          if (errorMessage === '') return;

          if (errorMessage !== UNKNOWN_ERROR_MESSAGE) {
            setAlertModal({
              content: errorMessage,
              ...(link && {
                onClickButton: () => {
                  router.push(link);
                },
              }),
            });
          }
        },
      }),
      queryCache: new QueryCache({
        onError(error, query) {
          // 아래 모달/리다이렉트 로직에 early return이 여러 개라 최상단에서 리포트해야 누락이 없다.
          // queryHash 원문은 queryKey의 파라미터 객체(로그인 아이디 등 사용자 입력)까지 담고 있어
          // 그대로 실으면 PII가 유출된다 — string/number 세그먼트만 남긴 스코프로 대체한다.
          reportApiWarning('query', error, { key: toQueryScope(query.queryKey) });

          if (error instanceof AuthorizationError && error.message === TOKEN_NOT_EXIST) {
            window.location.href = LINK.HOME;
            return;
          }
          if (error instanceof AxiosError) {
            if (error.response?.status === 429 && error.response.data?.code === 'tooManyRequests') {
              useRateLimitOverlayStoreBase
                .getState()
                .actions.trigger(resolveRetryAt(error.response.data));
              return;
            }
            const errorCode = error.response?.data?.code;
            const msg = errorMessage[errorCode];
            if (msg) {
              setAlertModal({
                content: msg || '알 수 없는 에러가 발생했습니다.',
              });
            }
          }
        },
      }),
    });
  }, []);

  useEffect(() => {
    setShowDevtools(true);
  }, []);

  // `errorInterceptor`(shared)는 react-query를 거치지 않는 요청까지 429를 잡지만, store를
  // 직접 부르면 app 레이어를 shared 그래프로 끌고 온다 — 그래서 signal만 쏘고, 그걸 오버레이
  // store에 꽂는 건 app 레이어인 여기 책임이다 (`Shared/network/rateLimitSignal` 주석 참고).
  useEffect(() => {
    setRateLimitListener((resetAt) => {
      useRateLimitOverlayStoreBase.getState().actions.trigger(resetAt);
    });

    return () => setRateLimitListener(undefined);
  }, []);

  return (
    <MSWProvider>
      <SDQueryProvider
        client={queryClient}
        policies={{
          infiniteQueryPolicy: defaultInfiniteQueryPolicy,
          mutationPolicy: defaultMutationPolicy,
          queryPolicy: defaultQueryPolicy,
        }}
      >
        {children}
        {showDevtools && <DynamicQueryDevtools />}
      </SDQueryProvider>
    </MSWProvider>
  );
}
